import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTrash, faEdit, faPause, faPlay } from '@fortawesome/free-solid-svg-icons';
import {
  Card,
  Form,
  Button,
  Table,
  InputGroup,
  FormControl,
  Badge,
  Row,
  Col,
  Modal
} from '@themesberg/react-bootstrap';
import api from '../../utils/api';

function pad(n) {
  return String(n).padStart(2, '0');
}

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toTimeValue(value) {
  if (!value) return '13:00';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '13:00';
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function nextDailyDate(timeValue) {
  const [hour, minute] = String(timeValue || '13:00').split(':').map((v) => parseInt(v, 10) || 0);
  const d = new Date();
  d.setSeconds(0, 0);
  d.setHours(hour, minute, 0, 0);
  if (d.getTime() <= Date.now()) {
    d.setDate(d.getDate() + 1);
  }
  return d;
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function inputValue(e) {
  const el = e && (e.target || e.currentTarget);
  return el && el.value != null ? el.value : '';
}

function inputChecked(e) {
  const el = e && (e.target || e.currentTarget);
  return !!(el && el.checked);
}

export default () => {
  const [formData, setFormData] = useState({
    selectedUsers: [],
    title: '',
    message: '',
    scheduleTime: false,
    isDaily: true,
    dailyTime: '13:00',
    oneShotAt: ''
  });

  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserSelect, setShowUserSelect] = useState(false);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const loadLists = useCallback(async () => {
    try {
      const [scheduledRes, sentRes] = await Promise.all([
        api.get('/notifications/scheduled'),
        api.get('/notifications/sent')
      ]);
      setScheduledNotifications(Array.isArray(scheduledRes.data) ? scheduledRes.data : []);
      setSentNotifications(Array.isArray(sentRes.data) ? sentRes.data : []);
    } catch (error) {
      console.error('Bildirim listesi yüklenemedi:', error);
    }
  }, []);

  useEffect(() => {
    loadLists();
    const interval = setInterval(loadLists, 30000);
    return () => clearInterval(interval);
  }, [loadLists]);

  useEffect(() => {
    if (!searchTerm.trim() || searchTerm.length < 2) {
      setFilteredUsers([]);
      return;
    }
    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await api.get('/users/admin-list');
        const users = res.data?.users || res.data || [];
        const term = searchTerm.toLowerCase();
        const results = users
          .filter((data) => data.isTestUser !== true && data.isAdmin !== true)
          .filter((data) => {
            const fullName = (data.name || data.firstName || '').toLowerCase();
            const emailStr = (data.email || '').toLowerCase();
            return fullName.includes(term) || emailStr.includes(term);
          })
          .map((data) => ({
            id: data.uid || data.id || data._id,
            name: data.name || data.firstName || 'İsimsiz',
            email: data.email || ''
          }));
        setFilteredUsers(results.slice(0, 50));
      } catch (error) {
        console.error('Üye arama hatası:', error);
      }
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  const resolveScheduledTime = () => {
    if (!formData.scheduleTime) return null;
    if (formData.isDaily) {
      return nextDailyDate(formData.dailyTime).toISOString();
    }
    if (!formData.oneShotAt) {
      throw new Error('Tarih ve saat seçin.');
    }
    const d = new Date(formData.oneShotAt);
    if (Number.isNaN(d.getTime())) {
      throw new Error('Geçerli bir tarih/saat girin.');
    }
    return d.toISOString();
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      alert('Başlık ve mesaj zorunludur.');
      return;
    }

    try {
      setLoading(true);
      const scheduledTime = resolveScheduledTime();
      const result = await api.post('/notifications/send', {
        title: formData.title.trim(),
        message: formData.message.trim(),
        userIds: formData.selectedUsers.length > 0 ? formData.selectedUsers : null,
        scheduledTime,
        isDaily: Boolean(scheduledTime && formData.isDaily)
      });

      setFormData({
        selectedUsers: [],
        title: '',
        message: '',
        scheduleTime: false,
        isDaily: true,
        dailyTime: '13:00',
        oneShotAt: ''
      });
      setShowUserSelect(false);
      await loadLists();

      if (scheduledTime) {
        alert(formData.isDaily
          ? 'Bildirim her gün seçilen saatte gönderilecek şekilde zamanlandı.'
          : 'Bildirim seçilen tarih ve saatte bir kez gönderilecek.');
      } else {
        const sentCount = result.data.sentCount || 0;
        alert(`Bildirim gönderildi. Ulaşan: ${sentCount}`);
      }
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Bildirim gönderilemedi.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`"${item.title}" silinsin mi?`)) return;
    await api.delete(`/notifications/${item.id}`);
    await loadLists();
  };

  const handleTogglePause = async (item) => {
    const next = item.status === 'paused' ? 'scheduled' : 'paused';
    await api.patch(`/notifications/${item.id}`, { status: next });
    await loadLists();
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    try {
      const scheduledTime = editItem.isDaily
        ? nextDailyDate(editItem.dailyTime || toTimeValue(editItem.scheduledTime)).toISOString()
        : new Date(editItem.oneShotAt || editItem.scheduledTime).toISOString();
      await api.patch(`/notifications/${editItem.id}`, {
        title: editItem.title,
        message: editItem.message,
        isDaily: Boolean(editItem.isDaily),
        scheduledTime
      });
      setEditItem(null);
      await loadLists();
    } catch (error) {
      alert(error.response?.data?.error || error.message || 'Güncellenemedi.');
    }
  };

  return (
    <>
      <div className="py-4">
        <h4>Bildirimler</h4>
        <p className="text-muted mb-0">Anında gönder veya ileri bir tarih/saate zamanla</p>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-4">Bildirim Gönder</h5>
          <Form onSubmit={handleSendNotification}>
            <Row className="mb-3">
              <Col md={12}>
                <Form.Label>Üye Seçin</Form.Label>
                <div className="mb-2">
                  <Button
                    variant={formData.selectedUsers.length === 0 ? 'primary' : 'outline-primary'}
                    size="sm"
                    type="button"
                    onClick={() => {
                      setFormData((prev) => ({ ...prev, selectedUsers: [] }));
                    }}
                  >
                    Herkese Gönder
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    type="button"
                    onClick={() => setShowUserSelect(!showUserSelect)}
                  >
                    {showUserSelect ? 'Listeyi Gizle' : 'Üye Seç'}
                  </Button>
                </div>

                {showUserSelect && (
                  <Card className="mt-2" style={{ maxHeight: 300, overflow: 'auto' }}>
                    <Card.Body>
                      <InputGroup className="mb-3">
                        <InputGroup.Text>
                          <FontAwesomeIcon icon={faSearch} />
                        </InputGroup.Text>
                        <FormControl
                          placeholder="Üye ara..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(inputValue(e))}
                        />
                      </InputGroup>
                      {filteredUsers.map((user) => (
                        <div key={user.id} className="mb-2">
                          <Form.Check
                            type="checkbox"
                            label={`${user.name} ${user.email ? `(${user.email})` : ''}`}
                            checked={formData.selectedUsers.includes(user.id)}
                            onChange={() => {
                              setFormData((prev) => {
                                const exists = prev.selectedUsers.includes(user.id);
                                return {
                                  ...prev,
                                  selectedUsers: exists
                                    ? prev.selectedUsers.filter((id) => id !== user.id)
                                    : [...prev.selectedUsers, user.id]
                                };
                              });
                            }}
                          />
                        </div>
                      ))}
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Başlık <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                placeholder="Bildirim başlığı"
                value={formData.title}
                onChange={(e) => {
                  const value = inputValue(e);
                  setFormData((prev) => ({ ...prev, title: value }));
                }}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mesaj <span className="text-danger">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Bildirim içeriği"
                value={formData.message}
                onChange={(e) => {
                  const value = inputValue(e);
                  setFormData((prev) => ({ ...prev, message: value }));
                }}
                required
              />
            </Form.Group>

            <div className="form-check mb-3">
              <input
                id="schedule-notification"
                type="checkbox"
                className="form-check-input"
                checked={!!formData.scheduleTime}
                onChange={(e) => {
                  const on = inputChecked(e);
                  setFormData((prev) => ({
                    ...prev,
                    scheduleTime: on,
                    isDaily: on ? true : prev.isDaily
                  }));
                }}
              />
              <label className="form-check-label" htmlFor="schedule-notification">
                Bildirimi Zamanla
              </label>
            </div>

            {formData.scheduleTime && (
              <Card className="bg-light border-0 mb-3">
                <Card.Body>
                  <div className="form-check mb-3">
                    <input
                      id="daily-notification"
                      type="checkbox"
                      className="form-check-input"
                      checked={!!formData.isDaily}
                      onChange={(e) => {
                        const on = inputChecked(e);
                        setFormData((prev) => ({ ...prev, isDaily: on }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="daily-notification">
                      Her gün gönder
                    </label>
                  </div>

                  {formData.isDaily ? (
                    <Form.Group>
                      <Form.Label>Gönderim saati</Form.Label>
                      <Form.Control
                        type="time"
                        style={{ maxWidth: 180 }}
                        value={formData.dailyTime}
                        onChange={(e) => {
                          const value = inputValue(e);
                          setFormData((prev) => ({ ...prev, dailyTime: value }));
                        }}
                      />
                      <small className="text-muted d-block mt-2">
                        Her gün bu saatte tekrarlanır. Saat geçmişse yarından itibaren başlar.
                      </small>
                    </Form.Group>
                  ) : (
                    <Form.Group>
                      <Form.Label>Tarih ve saat (tek seferlik)</Form.Label>
                      <Form.Control
                        type="datetime-local"
                        style={{ maxWidth: 280 }}
                        value={formData.oneShotAt}
                        onChange={(e) => {
                          const value = inputValue(e);
                          setFormData((prev) => ({ ...prev, oneShotAt: value }));
                        }}
                        required={formData.scheduleTime && !formData.isDaily}
                      />
                      <small className="text-muted d-block mt-2">
                        Seçilen anda bir kez gönderilir, sonra listeden düşer.
                      </small>
                    </Form.Group>
                  )}
                </Card.Body>
              </Card>
            )}

            <div className="d-flex justify-content-end">
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Kaydediliyor...' : (formData.scheduleTime ? 'Zamanla' : 'Gönder')}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3">Planlanmış Bildirimler</h5>
          {scheduledNotifications.length === 0 ? (
            <p className="text-muted mb-0">Planlanmış bildirim yok.</p>
          ) : (
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Başlık</th>
                  <th>İçerik</th>
                  <th>Zaman</th>
                  <th>Tip</th>
                  <th>Durum</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {scheduledNotifications.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.title}</strong></td>
                    <td><small>{item.message}</small></td>
                    <td>{formatDate(item.scheduledTime)}</td>
                    <td>
                      <Badge bg={item.isDaily ? 'success' : 'info'}>
                        {item.isDaily ? 'Her gün' : 'Tek sefer'}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg={item.status === 'paused' ? 'warning' : 'primary'}>
                        {item.status === 'paused' ? 'Pasif' : 'Aktif'}
                      </Badge>
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => setEditItem({
                        ...item,
                        dailyTime: toTimeValue(item.scheduledTime),
                        oneShotAt: toDatetimeLocal(item.scheduledTime)
                      })}>
                        <FontAwesomeIcon icon={faEdit} />
                      </Button>
                      <Button size="sm" variant="outline-warning" className="me-1" onClick={() => handleTogglePause(item)}>
                        <FontAwesomeIcon icon={item.status === 'paused' ? faPlay : faPause} />
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => handleDelete(item)}>
                        <FontAwesomeIcon icon={faTrash} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body>
          <h5 className="mb-3">Gönderilen Bildirimler</h5>
          {sentNotifications.length === 0 ? (
            <p className="text-muted mb-0">Henüz gönderilmiş bildirim yok.</p>
          ) : (
            sentNotifications.map((item) => (
              <div key={item.id} className="mb-3 p-3 border rounded">
                <div className="d-flex justify-content-between">
                  <div>
                    <strong>{item.title}</strong>
                    <p className="mb-1 text-muted">{item.message}</p>
                    <small className="text-muted">
                      {item.sentCount || 0} kişi • {formatDate(item.sentAt || item.createdAt)}
                    </small>
                  </div>
                  <Badge bg="success">Gönderildi</Badge>
                </div>
              </div>
            ))
          )}
        </Card.Body>
      </Card>

      <Modal show={!!editItem} onHide={() => setEditItem(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Bildirimi Düzenle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editItem && (
            <>
              <Form.Group className="mb-3">
                <Form.Label>Başlık</Form.Label>
                <Form.Control
                  value={editItem.title || ''}
                  onChange={(e) => {
                    const value = inputValue(e);
                    setEditItem((prev) => ({ ...prev, title: value }));
                  }}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Mesaj</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editItem.message || ''}
                  onChange={(e) => {
                    const value = inputValue(e);
                    setEditItem((prev) => ({ ...prev, message: value }));
                  }}
                />
              </Form.Group>
              <div className="form-check mb-3">
                <input
                  id="edit-daily"
                  type="checkbox"
                  className="form-check-input"
                  checked={!!editItem.isDaily}
                  onChange={(e) => {
                    const on = inputChecked(e);
                    setEditItem((prev) => ({ ...prev, isDaily: on }));
                  }}
                />
                <label className="form-check-label" htmlFor="edit-daily">Her gün gönder</label>
              </div>
              {editItem.isDaily ? (
                <Form.Control
                  type="time"
                  value={editItem.dailyTime || '13:00'}
                  onChange={(e) => {
                    const value = inputValue(e);
                    setEditItem((prev) => ({ ...prev, dailyTime: value }));
                  }}
                />
              ) : (
                <Form.Control
                  type="datetime-local"
                  value={editItem.oneShotAt || ''}
                  onChange={(e) => {
                    const value = inputValue(e);
                    setEditItem((prev) => ({ ...prev, oneShotAt: value }));
                  }}
                />
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditItem(null)}>Vazgeç</Button>
          <Button variant="primary" onClick={handleSaveEdit}>Kaydet</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
